import React, { useState } from 'react';
import { Home, ShoppingBag, Package, User, Bell, LogOut, FileText, Grid3x3, List, LayoutDashboard, DollarSign, Warehouse as WarehouseIcon, Users, Truck, BarChart3, MapPin, ChevronLeft, ChevronRight, Zap, Activity, Search, Building2, TrendingUp, Crown, Factory, ArrowDownToLine, ArrowUpFromLine, Navigation, Menu, X } from 'lucide-react';
interface UnifiedLayoutProps {
  children: React.ReactNode;
  role: 'buyer' | 'admin' | 'superadmin' | 'logistics' | 'factory' | 'warehouse';
  activeRoute: string;
  onNavigate: (route: string) => void;
  onLogout: () => void;
}
export function UnifiedLayout({
  children,
  role,
  activeRoute,
  onNavigate,
  onLogout
}: UnifiedLayoutProps) {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // Unified menu structure for all 6 roles
  const buyerMenu = [{
    id: 'home',
    label: 'Beranda',
    icon: Home,
    path: '/'
  }, {
    id: 'catalog',
    label: 'Katalog',
    icon: ShoppingBag,
    path: '/catalog'
  }, {
    id: 'bulk-order',
    label: 'Bulk Order',
    icon: Grid3x3,
    path: '/bulk-order'
  }, {
    id: 'order-management',
    label: 'Kelola Order',
    icon: List,
    path: '/order-management'
  }, {
    id: 'tracking',
    label: 'Tracking',
    icon: Package,
    path: '/tracking'
  }, {
    id: 'history',
    label: 'Riwayat',
    icon: FileText,
    path: '/history'
  }, {
    id: 'account',
    label: 'Akun',
    icon: User,
    path: '/account'
  }];
  const adminMenu = [{
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    path: '/admin/dashboard'
  }, {
    id: 'order-tracking',
    label: 'Tracking',
    icon: MapPin,
    path: '/admin/order-tracking'
  }, {
    id: 'products',
    label: 'Produk',
    icon: Package,
    path: '/admin/products'
  }, {
    id: 'pricing',
    label: 'Harga',
    icon: DollarSign,
    path: '/admin/pricing'
  }, {
    id: 'po-consolidation',
    label: 'PO',
    icon: FileText,
    path: '/admin/po-consolidation'
  }, {
    id: 'warehouse',
    label: 'Gudang',
    icon: WarehouseIcon,
    path: '/admin/warehouse'
  }, {
    id: 'cooperatives',
    label: 'Koperasi',
    icon: Users,
    path: '/admin/cooperatives'
  }, {
    id: 'logistics',
    label: 'Logistik',
    icon: Truck,
    path: '/admin/logistics'
  }, {
    id: 'reports',
    label: 'Laporan',
    icon: BarChart3,
    path: '/admin/reports'
  }];
  const superadminMenu = [{
    id: 'dashboard',
    label: 'Overview',
    icon: LayoutDashboard,
    path: '/superadmin/dashboard'
  }, {
    id: 'agregators',
    label: 'Agregator',
    icon: Building2,
    path: '/superadmin/agregators'
  }, {
    id: 'revenue',
    label: 'Revenue',
    icon: TrendingUp,
    path: '/superadmin/revenue'
  }, {
    id: 'system',
    label: 'System',
    icon: Activity,
    path: '/superadmin/system'
  }];
  const logisticsMenu = [{
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    path: '/logistics/dashboard'
  }, {
    id: 'shipments',
    label: 'Shipments',
    icon: Truck,
    path: '/logistics/shipments'
  }, {
    id: 'tracking',
    label: 'Tracking',
    icon: Navigation,
    path: '/logistics/tracking'
  }, {
    id: 'performance',
    label: 'Performance',
    icon: BarChart3,
    path: '/logistics/performance'
  }];
  const factoryMenu = [{
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    path: '/factory/dashboard'
  }, {
    id: 'purchase-orders',
    label: 'PO',
    icon: FileText,
    path: '/factory/purchase-orders'
  }, {
    id: 'production',
    label: 'Production',
    icon: Factory,
    path: '/factory/production'
  }, {
    id: 'inventory',
    label: 'Inventory',
    icon: Package,
    path: '/factory/inventory'
  }];
  const warehouseMenu = [{
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    path: '/warehouse/dashboard'
  }, {
    id: 'inbound',
    label: 'Inbound',
    icon: ArrowDownToLine,
    path: '/warehouse/inbound'
  }, {
    id: 'outbound',
    label: 'Outbound',
    icon: ArrowUpFromLine,
    path: '/warehouse/outbound'
  }, {
    id: 'inventory',
    label: 'Inventory',
    icon: Package,
    path: '/warehouse/inventory'
  }];
  const menuItems = role === 'buyer' ? buyerMenu : role === 'admin' ? adminMenu : role === 'superadmin' ? superadminMenu : role === 'logistics' ? logisticsMenu : role === 'factory' ? factoryMenu : warehouseMenu;
  // Compact theme colors
  const theme = {
    buyer: {
      sidebarBg: 'bg-white',
      sidebarBorder: 'border-slate-200',
      sidebarText: 'text-slate-600',
      activeItemBg: 'bg-blue-50',
      activeItemText: 'text-blue-700',
      hoverBg: 'hover:bg-slate-50',
      logoBg: 'bg-blue-600',
      topBarBg: 'bg-white',
      topBarBorder: 'border-slate-200',
      mobileOverlay: 'bg-black/50'
    },
    admin: {
      sidebarBg: 'bg-slate-900',
      sidebarBorder: 'border-slate-700',
      sidebarText: 'text-slate-400',
      activeItemBg: 'bg-blue-600',
      activeItemText: 'text-white',
      hoverBg: 'hover:bg-slate-800',
      logoBg: 'bg-blue-600',
      topBarBg: 'bg-slate-900',
      topBarBorder: 'border-slate-700',
      mobileOverlay: 'bg-black/70'
    },
    superadmin: {
      sidebarBg: 'bg-violet-900',
      sidebarBorder: 'border-violet-700',
      sidebarText: 'text-violet-300',
      activeItemBg: 'bg-violet-600',
      activeItemText: 'text-white',
      hoverBg: 'hover:bg-violet-800',
      logoBg: 'bg-violet-600',
      topBarBg: 'bg-violet-900',
      topBarBorder: 'border-violet-700',
      mobileOverlay: 'bg-black/70'
    },
    logistics: {
      sidebarBg: 'bg-green-900',
      sidebarBorder: 'border-green-700',
      sidebarText: 'text-green-300',
      activeItemBg: 'bg-green-600',
      activeItemText: 'text-white',
      hoverBg: 'hover:bg-green-800',
      logoBg: 'bg-green-600',
      topBarBg: 'bg-green-900',
      topBarBorder: 'border-green-700',
      mobileOverlay: 'bg-black/70'
    },
    factory: {
      sidebarBg: 'bg-orange-900',
      sidebarBorder: 'border-orange-700',
      sidebarText: 'text-orange-300',
      activeItemBg: 'bg-orange-600',
      activeItemText: 'text-white',
      hoverBg: 'hover:bg-orange-800',
      logoBg: 'bg-orange-600',
      topBarBg: 'bg-orange-900',
      topBarBorder: 'border-orange-700',
      mobileOverlay: 'bg-black/70'
    },
    warehouse: {
      sidebarBg: 'bg-cyan-900',
      sidebarBorder: 'border-cyan-700',
      sidebarText: 'text-cyan-300',
      activeItemBg: 'bg-cyan-600',
      activeItemText: 'text-white',
      hoverBg: 'hover:bg-cyan-800',
      logoBg: 'bg-cyan-600',
      topBarBg: 'bg-cyan-900',
      topBarBorder: 'border-cyan-700',
      mobileOverlay: 'bg-black/70'
    }
  };
  const t = theme[role];
  const roleConfig = {
    buyer: {
      icon: 'K',
      name: 'KDMP',
      subtitle: 'Portal'
    },
    admin: {
      icon: <Zap className="w-4 h-4" data-id="element-205" />,
      name: 'PTX',
      subtitle: 'Admin'
    },
    superadmin: {
      icon: <Crown className="w-4 h-4" data-id="element-206" />,
      name: 'Super',
      subtitle: 'Admin'
    },
    logistics: {
      icon: <Truck className="w-4 h-4" data-id="element-207" />,
      name: 'JNE',
      subtitle: 'Logistics'
    },
    factory: {
      icon: <Factory className="w-4 h-4" data-id="element-208" />,
      name: 'Factory',
      subtitle: 'Indofood'
    },
    warehouse: {
      icon: <WarehouseIcon className="w-4 h-4" data-id="element-209" />,
      name: 'Warehouse',
      subtitle: 'Bogor'
    }
  };
  const config = roleConfig[role];
  const handleNavigate = (path: string) => {
    onNavigate(path);
    setIsMobileMenuOpen(false); // Close mobile menu after navigation
  };
  return <div className="flex h-screen bg-slate-50 overflow-hidden" data-id="element-210">
      {/* Mobile Overlay */}
      {isMobileMenuOpen && <div className={`fixed inset-0 ${t.mobileOverlay} z-40 lg:hidden`} onClick={() => setIsMobileMenuOpen(false)} data-id="element-211" />}

      {/* RESPONSIVE Sidebar */}
      <aside className={`
          ${t.sidebarBg} border-r ${t.sidebarBorder} flex flex-col flex-shrink-0 transition-all duration-300 z-50
          ${isSidebarCollapsed ? 'w-14' : 'w-52'}
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
          fixed lg:relative h-full
        `} data-id="element-212">
        {/* Compact Logo */}
        <div className={`h-12 flex items-center px-3 border-b ${t.sidebarBorder} flex-shrink-0`} data-id="element-213">
          {!isSidebarCollapsed ? <div className="flex items-center gap-2 w-full" data-id="element-214">
              <div className={`w-7 h-7 ${t.logoBg} rounded-lg flex items-center justify-center text-white font-bold text-xs flex-shrink-0`} data-id="element-215">
                {config.icon}
              </div>
              <div className="min-w-0 flex-1" data-id="element-216">
                <h1 className={`font-bold text-sm leading-tight truncate ${role === 'buyer' ? 'text-slate-900' : 'text-white'}`} data-id="element-217">
                  {config.name}
                </h1>
                <p className={`text-xs leading-tight ${role === 'buyer' ? 'text-slate-500' : 'text-slate-400'}`} data-id="element-218">
                  {config.subtitle}
                </p>
              </div>
              {/* Mobile Close Button */}
              <button onClick={() => setIsMobileMenuOpen(false)} className="lg:hidden p-1 rounded hover:bg-slate-700/50" data-id="element-219">
                <X className={`w-4 h-4 ${role === 'buyer' ? 'text-slate-600' : 'text-white'}`} data-id="element-220" />
              </button>
            </div> : <div className={`w-7 h-7 ${t.logoBg} rounded-lg flex items-center justify-center text-white font-bold text-xs mx-auto`} data-id="element-221">
              {config.icon}
            </div>}
        </div>

        {/* Desktop Toggle Button */}
        <button onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)} className={`hidden lg:block absolute -right-2.5 top-14 w-5 h-5 ${role === 'buyer' ? 'bg-slate-200 hover:bg-slate-300 text-slate-600' : 'bg-slate-700 hover:bg-slate-600 text-white'} border border-white rounded-full items-center justify-center transition-all z-10 shadow`} data-id="element-222">
          {isSidebarCollapsed ? <ChevronRight className="w-3 h-3" data-id="element-223" /> : <ChevronLeft className="w-3 h-3" data-id="element-224" />}
        </button>

        {/* Compact Navigation */}
        <nav className="flex-1 py-2 px-2 flex flex-col gap-0.5 overflow-y-auto" data-id="element-225">
          {menuItems.map(item => {
          const Icon = item.icon;
          const isActive = activeRoute === item.path;
          return <button key={item.id} onClick={() => handleNavigate(item.path)} className={`
                  w-full flex items-center gap-2 px-2 py-2 rounded-lg text-xs font-medium transition-all
                  ${isActive ? `${t.activeItemBg} ${t.activeItemText}` : `${t.sidebarText} ${t.hoverBg}`}
                  ${isSidebarCollapsed ? 'justify-center' : ''}
                `} title={isSidebarCollapsed ? item.label : ''} data-id="element-226">
                <Icon className="w-4 h-4 flex-shrink-0" data-id="element-227" />
                {!isSidebarCollapsed && <span className="truncate" data-id="element-228">{item.label}</span>}
              </button>;
        })}
        </nav>

        {/* Compact Bottom Actions */}
        <div className={`p-2 border-t ${t.sidebarBorder} flex-shrink-0`} data-id="element-229">
          {!isSidebarCollapsed ? <div className="space-y-1" data-id="element-230">
              <button className={`w-full flex items-center gap-2 px-2 py-2 rounded-lg text-xs font-medium ${t.sidebarText} ${t.hoverBg} transition-colors`} data-id="element-231">
                <Bell className="w-4 h-4" data-id="element-232" />
                Notifikasi
              </button>
              <button onClick={onLogout} className="w-full flex items-center gap-2 px-2 py-2 rounded-lg text-xs font-medium text-red-600 hover:bg-red-50 transition-colors" data-id="element-233">
                <LogOut className="w-4 h-4" data-id="element-234" />
                Keluar
              </button>
            </div> : <div className="space-y-1" data-id="element-235">
              <button className={`w-full p-2 rounded-lg ${t.hoverBg} transition-colors flex justify-center`} data-id="element-236">
                <Bell className="w-4 h-4" data-id="element-237" />
              </button>
              <button onClick={onLogout} className="w-full p-2 rounded-lg hover:bg-red-50 transition-colors flex justify-center" data-id="element-238">
                <LogOut className="w-4 h-4 text-red-600" data-id="element-239" />
              </button>
            </div>}
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden" data-id="element-240">
        {/* RESPONSIVE Top Bar */}
        <header className={`h-12 ${t.topBarBg} border-b ${t.topBarBorder} flex items-center justify-between px-3 md:px-4 shadow-sm flex-shrink-0`} data-id="element-241">
          <div className="flex items-center gap-2 min-w-0 flex-1" data-id="element-242">
            {/* Mobile Menu Button */}
            <button onClick={() => setIsMobileMenuOpen(true)} className={`lg:hidden p-1.5 rounded-md ${role === 'buyer' ? 'text-slate-600 hover:bg-slate-100' : 'text-white hover:bg-slate-700/50'} transition-colors`} data-id="element-243">
              <Menu className="w-5 h-5" data-id="element-244" />
            </button>

            <h2 className={`text-sm font-bold truncate ${role === 'buyer' ? 'text-slate-900' : 'text-white'}`} data-id="element-245">
              {menuItems.find(m => m.path === activeRoute)?.label || config.name}
            </h2>
          </div>

          <div className="flex items-center gap-1 md:gap-2" data-id="element-246">
            {/* Compact Search - Hidden on mobile */}
            {role !== 'buyer' && <div className="relative hidden md:block" data-id="element-247">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" data-id="element-248" />
                <input type="text" placeholder="Cari..." className={`w-32 lg:w-48 pl-7 pr-2 py-1.5 rounded-md border text-xs ${role === 'buyer' ? 'border-slate-200' : 'border-slate-600 bg-slate-800/50 placeholder-slate-400 text-white'} focus:outline-none focus:ring-1 focus:ring-blue-500`} data-id="element-249" />
              </div>}

            {/* Compact Status - Hidden on mobile */}
            {role !== 'buyer' && <div className={`hidden md:flex items-center gap-1 px-2 py-1 ${role === 'superadmin' ? 'bg-violet-500/20' : role === 'logistics' ? 'bg-green-500/20' : role === 'factory' ? 'bg-orange-500/20' : role === 'warehouse' ? 'bg-cyan-500/20' : 'bg-green-500/20'} rounded-md`} data-id="element-250">
                <Activity className={`w-3 h-3 ${role === 'superadmin' ? 'text-violet-400' : role === 'logistics' ? 'text-green-400' : role === 'factory' ? 'text-orange-400' : role === 'warehouse' ? 'text-cyan-400' : 'text-green-400'}`} data-id="element-251" />
                <span className={`text-xs font-medium ${role === 'superadmin' ? 'text-violet-400' : role === 'logistics' ? 'text-green-400' : role === 'factory' ? 'text-orange-400' : role === 'warehouse' ? 'text-cyan-400' : 'text-green-400'}`} data-id="element-252">
                  Active
                </span>
              </div>}

            {/* Compact Credit (Buyer only) - Responsive */}
            {role === 'buyer' && <div className="flex items-center gap-1.5 md:gap-2 px-2 md:px-3 py-1.5 bg-slate-50 rounded-md border border-slate-200" data-id="element-253">
                <div data-id="element-254">
                  <p className="text-xs text-slate-500 leading-tight hidden md:block" data-id="element-255">
                    Kredit
                  </p>
                  <p className="text-xs font-bold text-slate-900 leading-tight" data-id="element-256">
                    37.5Jt
                  </p>
                </div>
              </div>}

            {/* Compact Notifications */}
            <button className={`relative p-1.5 ${role === 'buyer' ? 'text-slate-600 hover:bg-slate-100' : 'text-slate-300 hover:bg-slate-700/50'} rounded-md transition-colors`} data-id="element-257">
              <Bell className="w-4 h-4" data-id="element-258" />
              <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 bg-red-500 rounded-full" data-id="element-259"></span>
            </button>

            {/* Compact User Profile */}
            <div className={`flex items-center gap-1.5 md:gap-2 pl-1.5 md:pl-2 border-l ${role === 'buyer' ? 'border-slate-200' : 'border-slate-700'}`} data-id="element-260">
              <div className={`w-7 h-7 ${role === 'superadmin' ? 'bg-violet-600' : role === 'logistics' ? 'bg-green-600' : role === 'factory' ? 'bg-orange-600' : role === 'warehouse' ? 'bg-cyan-600' : role === 'admin' ? 'bg-blue-600' : 'bg-blue-600'} rounded-md flex items-center justify-center font-bold text-white text-xs`} data-id="element-261">
                {role === 'buyer' ? 'BS' : role === 'admin' ? 'AO' : role === 'superadmin' ? <Crown className="w-4 h-4" data-id="element-262" /> : role === 'logistics' ? <Truck className="w-3 h-3" data-id="element-263" /> : role === 'factory' ? <Factory className="w-3 h-3" data-id="element-264" /> : <WarehouseIcon className="w-3 h-3" data-id="element-265" />}
              </div>
            </div>
          </div>
        </header>

        {/* RESPONSIVE Page Content */}
        <main className="flex-1 overflow-y-auto p-3 md:p-4" data-id="element-266">
          <div className="mx-auto w-full max-w-[1400px]" data-id="element-267">{children}</div>
        </main>
      </div>
    </div>;
}